// Pure ordering rules for the "Now" task on Today (Focus concept), kept free of React so they're testable.
import { PRIORITY_RANK, type TaskWithFlags } from "@desk/contracts";

/**
 * A Skip snapshot for one day. `base` is the underlying order (priority or manual) when the user
 * last skipped; `order` is what was shown after that skip. Stored per device.
 */
export interface SkipState {
  date: string;
  base: string[];
  order: string[];
}

/**
 * Applies Skip reordering to today's underlying order.
 *
 * - Tasks known to the snapshot keep their skipped order; new tasks keep their own slots.
 * - Completed/removed tasks simply drop out, so the next task moves up.
 * - If the underlying order of the known tasks changed (priority edit, Start pinning a task,
 *   a manual drag), the snapshot is stale and the underlying order wins.
 */
export function applySkips(base: string[], skip: SkipState | null, date: string): string[] {
  if (!skip || skip.date !== date || skip.order.length === 0) return base;
  const now = new Set(base);
  const before = new Set(skip.base);
  const was = skip.base.filter((id) => now.has(id));
  const is = base.filter((id) => before.has(id));
  if (was.length !== is.length || was.some((id, i) => id !== is[i])) return base;
  const rank = new Map(skip.order.map((id, i) => [id, i]));
  const known = base.filter((id) => rank.has(id)).sort((a, b) => rank.get(a)! - rank.get(b)!);
  let k = 0;
  return base.map((id) => (rank.has(id) ? known[k++]! : id));
}

/** Skip: moves `id` down one place in the shown order (never removes it). Returns the new snapshot. */
export function skipDown(base: string[], skip: SkipState | null, date: string, id: string): SkipState {
  const order = applySkips(base, skip, date).slice();
  const i = order.indexOf(id);
  if (i >= 0 && i < order.length - 1) [order[i], order[i + 1]] = [order[i + 1]!, order[i]!];
  return { date, base: base.slice(), order };
}

/** Orders tasks by a list of ids (ids not in the list are dropped). */
export function byIds<T extends { id: string }>(tasks: T[], ids: string[]): T[] {
  const m = new Map(tasks.map((t) => [t.id, t]));
  return ids.map((id) => m.get(id)).filter((t): t is T => !!t);
}

/**
 * "Pull next" candidates: open (To do / In progress), not snoozed, not deleted, not already on
 * today's plan. Highest priority first, then the nearest deadline, then the list's own order.
 */
export function rankPullNext(tasks: TaskWithFlags[], exclude: Set<string>): TaskWithFlags[] {
  const index = new Map(tasks.map((t, i) => [t.id, i]));
  return tasks
    .filter(
      (t) =>
        (t.status === "todo" || t.status === "in_progress") &&
        !t.flags.isSnoozed &&
        !t.deletedAt &&
        !exclude.has(t.id),
    )
    .sort((a, b) => {
      const p = PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority];
      if (p) return p;
      if (a.deadline !== b.deadline) {
        if (!a.deadline) return 1;
        if (!b.deadline) return -1;
        return a.deadline.localeCompare(b.deadline);
      }
      return index.get(a.id)! - index.get(b.id)!;
    });
}
